import React from 'react';
import { Container, Paper, Box, Typography, Button, Divider, AppBar, Toolbar, IconButton, Alert } from '@mui/material';
import { IoMoonSharp, IoSunnyOutline } from 'react-icons/io5';
import { useAuth } from '../auth/AuthProvider';
import { useThemeContext } from '../contexts/ThemeContext';
import CRTEffect from '../components/CRTEffect';

export const LoginPage: React.FC = () => {
  const { signInWithAzure, signInWithCognito, loading } = useAuth();
  const { themeMode, toggleTheme } = useThemeContext();

  const isAzureEnabled = process.env.REACT_APP_AZURE_SSO_ENABLED === 'true';
  const cognitoDomain = process.env.REACT_APP_COGNITO_DOMAIN;
  const cognitoClientId = process.env.REACT_APP_CLIENT_ID;

  return (
    <CRTEffect>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppBar position="static">
          <Toolbar>
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
              <Box
                component="img"
                src="/logo-white-transparent.svg"
                alt="HK Workflows"
                sx={{ 
                  height: 40
                }}
              />
            </Box>
            <IconButton 
              color="inherit" 
              onClick={(e) => toggleTheme(e.ctrlKey)} 
              title={`Current: ${themeMode}${themeMode !== 'retro' ? ' (Ctrl+click for retro)' : ''}`}
            >
              {themeMode === 'light' ? <IoMoonSharp size={24} /> : themeMode === 'dark' ? <IoSunnyOutline size={24} /> : '📺'}
            </IconButton>
          </Toolbar>
        </AppBar>
        
        {(process.env.REACT_APP_ENVIRONMENT === 'dev' || process.env.REACT_APP_ENVIRONMENT === 'testing') && (
          <Alert severity="info" sx={{ borderRadius: 0, backgroundColor: process.env.REACT_APP_ENVIRONMENT === 'dev' ? '#1976d2' : '#ed6c02', color: 'white' }}>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {process.env.REACT_APP_ENVIRONMENT?.toUpperCase()} ENVIRONMENT
            </Typography>
          </Alert>
        )}
        
        <Container maxWidth="sm" sx={{ mt: 8, mb: 4, flex: 1, display: 'flex', alignItems: 'center' }}>
          <Paper sx={{ p: 6, width: '100%', textAlign: 'center' }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <Box
                component="img"
                src="/logo-contraption.svg"
                alt="HK Workflows"
                sx={{ 
                  height: 80,
                  maxWidth: '100%'
                }}
              />
            </Box>
            <Typography variant="h6" color="text.secondary" gutterBottom sx={{ mb: 4 }}>
              Generative AI Orchestration
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 300, mx: 'auto' }}>
              {isAzureEnabled && (
                <Button
                  variant="contained"
                  size="large"
                  onClick={signInWithAzure}
                  disabled={loading}
                  sx={{ 
                    py: 1.5,
                    minHeight: 56,
                    fontSize: '1.1rem',
                    fontWeight: 600
                  }}
                >
                  Sign in with Microsoft
                </Button>
              )}
              
              {isAzureEnabled && cognitoDomain && cognitoClientId && (
                <Divider sx={{ my: 1 }}>or</Divider>
              )}
              
              {cognitoDomain && cognitoClientId && (
                <Button
                  variant="outlined"
                  size="large"
                  onClick={signInWithCognito}
                  disabled={loading}
                  sx={{ 
                    py: 1.5,
                    minHeight: 56,
                    fontSize: '1.1rem',
                    fontWeight: 600
                  }}
                >
                  Sign in with AWS Cognito
                </Button>
              )}
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
              Choose your preferred authentication method to continue
            </Typography>
          </Paper>
        </Container>
        
        <Box component="footer" sx={{ 
          py: 2, 
          px: 3, 
          backgroundColor: 'primary.main', 
          color: 'white',
          textAlign: 'center'
        }}>
          <Typography variant="body2" color='white'>
            For technical assistance or to give feedback, please contact{' '}
            <a href="mailto:innovation@hklaw.com" style={{ color: 'inherit', textDecoration: 'underline' }}>
              innovation@hklaw.com
            </a>
          </Typography>
        </Box>
      </Box>
    </CRTEffect>
  );
};